import { SiteUrls } from '@tableau/shared-urls';
import { IUserNotification, IUserNotificationContentProvider, NotificationContentProviders, UserNotificationType } from '@tableau/user-notifications-client-core';
import { SkeletonLoaderWidget, TabActivationMode, TabAndPanelContents, TabListAlignment, TabsWidget } from '@tableau/widgets';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import tabbable from 'tabbable';
import _ from 'underscore';

import { errorBoundary, sendTelemetryErrorEvent } from '~commonInfra/components/ErrorBoundary';
import { imgPath } from '~commonInfra/utils/ImgUrl';
import { Keys } from '~commonInfra/utils/Keys';
import { Location } from '~commonInfra/utils/Location';

import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { IUserNotificationMenuItemProvider } from '../menuProviders/IUserNotificationMenuItemProvider';
import { NotificationMenuitemProviders } from '../menuProviders/NotificationMenuProviders';
import { UserNotificationActions } from '../redux/UserNotificationActions';
import { NotificationCenterStyles } from '../styles/notificationCenterStyles';
import * as notificationStyles from '../styles/notificationStyles';
import { sendUserNotificationsCenterPerfTelemetry, sendUserNotificationsPanelTelemetry, UserNotificationsTelemetryEvents } from '../telemetry/UserNotificationsTelemetry';
import { getEarlyNotificationTime, isNullOrUndefined } from '../utility/notificationHelper';
import { Notification } from './Notification';
import { NotificationLoader } from './NotificationLoader';
import { NotificationLoadFail } from './NotificationLoadFail';
import { NotificationPanelHeader, NotificationPanelTitleDomId } from './NotificationPanelHeader';

//This state is used to control when to show the loading and failure state
export enum NotificationDisplayMode {
  NotificationLoaded,
  NotificationLoading,
  NotificationLoadFailed
}

export type NotificationPanelProps = {
  siteUrls: SiteUrls;
  userNotificationActions: UserNotificationActions;
  displayMode: NotificationDisplayMode;
  closePanel: () => void;
  domainName: string;
  userName: string;
  userId: string;
  siteLuid: string;
};

export type NotificationPanelState = {
  nextPageToken: string;
  notifications: Array<IUserNotification<any>>;
  currentTabIndex: number;
  markAllAsReadClickTime: number;
  unreadCount: number;
  focusableNotificationIndex: number;
  shouldFocusOnActionButton: boolean;
};

export enum NotificationStatus {
  READ,
  UNREAD,
  ALL
}

enum NotificationSection {
  Earlier = 'earlier',
  New = 'new'
}

export const pageSize = 5;

export class NotificationPanel extends React.PureComponent<NotificationPanelProps, NotificationPanelState> {
  private notificationPanelRef: React.RefObject<HTMLDivElement> = React.createRef();
  private tabsRef: TabsWidget | null = null;

  private unsubscribeFromAppStore: () => void;
  private notificationContentProviders: _.Dictionary<IUserNotificationContentProvider<any>>;
  private notificationMenuItemProviders: _.Dictionary<IUserNotificationMenuItemProvider<any>>;
  private tabsDimensionOverrides = {
    tabHeight: 32,
    tabPanelPadding: '0'
  };
  private loadStartTime: number;

  constructor(props: NotificationPanelProps) {
    super(props);
    this.notificationContentProviders = NotificationContentProviders();
    this.notificationMenuItemProviders = NotificationMenuitemProviders(this.props.userNotificationActions);
    this.state = {
      nextPageToken: null,
      notifications: [],
      currentTabIndex: 0,
      markAllAsReadClickTime: 0,
      unreadCount: 0,
      focusableNotificationIndex: 0,
      shouldFocusOnActionButton: false,
    };
    this.renderNotifications = this.renderNotifications.bind(this);
    this.onAppStoreChange = this.onAppStoreChange.bind(this);
    this.onTabChange = this.onTabChange.bind(this);
    this.getNotificationsForCurrentTab = this.getNotificationsForCurrentTab.bind(this);
    this.loadStartTime = performance.now();
  }

  public componentDidMount() {
    this.unsubscribeFromAppStore = getAppStore().subscribe(this.onAppStoreChange);
  }

  public componentWillUnmount() {
    this.unsubscribeFromAppStore();
  }

  public componentDidUpdate(prevProps: NotificationPanelProps, prevState: NotificationPanelState) {
    //record panel load time if the notification center is opened or if the notification panel refreshes with new notifications
    if (this.state.notifications.length > prevState.notifications.length) {
      const loadTime = performance.now() - this.loadStartTime;
      sendUserNotificationsCenterPerfTelemetry(UserNotificationsTelemetryEvents.notificationCenterPerfLoadTime,
        this.props.userId, this.props.siteLuid, loadTime, this.state.notifications.length);
    }

    //record panel load time if mark all as read is clicked and there were multiple unread notifications
    if (prevState.unreadCount > 1 && this.state.unreadCount === 0 && this.state.markAllAsReadClickTime > 0) {
      const loadTime = performance.now() - this.state.markAllAsReadClickTime;
      sendUserNotificationsCenterPerfTelemetry(UserNotificationsTelemetryEvents.markAllAsReadPerfLoadTime,
        this.props.userId, this.props.siteLuid, loadTime, this.state.notifications.length);
    }

    // Since notification items are rendered following the grid pattern, there should be only be one tab stop at any time.
    // In the state of this component, we have a `focusableNotificationIndex` to track which notification item should be focusable
    // and a `shouldFocusOnActionButton` to track whether the focus should be on the item div or the action button associated with the item.
    // The following logic moves focus to the expected element whenever the items on the current tab get updated,
    // or whenever `focusableNotificationIndex` or `shouldFocusOnActionButton` gets updated.

    const itemCountOnCurrentTab = this.getNotificationsForCurrentTab().length;

    // adjust the `focusableNotificationIndex` when it is invalid (typically after notification items are removed or marked as read)
    if ((this.state.currentTabIndex === 0 && prevState.notifications.length !== this.state.notifications.length)
      || (this.state.currentTabIndex === 1 && prevState.unreadCount !== this.state.unreadCount)
    ) {
      if (this.state.focusableNotificationIndex >= itemCountOnCurrentTab) {
        this.setState({ focusableNotificationIndex: Math.max(0, this.state.focusableNotificationIndex - 1) });
      }
      if (itemCountOnCurrentTab === 0 && this.notificationPanelRef.current?.contains(document.activeElement) === false) {
        // all items have been removed and focus is lost, so set focus on the tabs again
        this.tabsRef?.setFocusedIndex(this.state.currentTabIndex);
      }
    }

    // set focus on a different notification item (or move focus between the item and the action button) when the new `focusableNotificationIndex` is valid
    if ((prevState.focusableNotificationIndex !== this.state.focusableNotificationIndex || prevState.shouldFocusOnActionButton !== this.state.shouldFocusOnActionButton)
      && prevState.currentTabIndex === this.state.currentTabIndex
      && this.state.focusableNotificationIndex < itemCountOnCurrentTab
    ) {
      const focusableNotificationItem = this.notificationPanelRef.current?.querySelectorAll<HTMLDivElement>('[role="row"]')[this.state.focusableNotificationIndex];
      tabbable(focusableNotificationItem)[0].focus();
    }
  }

  private onAppStoreChange() {
    const { notifications, nextPageToken } = getAppStore().getState().notificationState;
    if (notifications !== null) {
      let notificationValues = _.values(notifications);
      const newUnreadCount = notificationValues.filter((n) => !n.read).length;
      if (notificationValues.length !== this.state.notifications.length || newUnreadCount !== this.state.unreadCount) {
        this.setState({
          nextPageToken: nextPageToken,
          notifications: notificationValues,
          unreadCount: newUnreadCount,
        });
      }
    }
  }

  private getNotificationsForCurrentTab() {
    return (this.state.currentTabIndex === 0 ? this.state.notifications : _.filter(this.state.notifications, (value: IUserNotification<any>) => !value.read))
      .filter(notification => Object.values(UserNotificationType).includes(notification.type));
  }

  private onTabChange(tabIndex: number): void {
    sendUserNotificationsPanelTelemetry(UserNotificationsTelemetryEvents.changeTabs, this.props.userId, this.props.siteLuid, tabIndex);
    this.setState({ currentTabIndex: tabIndex, focusableNotificationIndex: 0, shouldFocusOnActionButton: false });
  }

  private updateMarkAllAsReadClickTime(currentTime: number): void {
    this.setState({ markAllAsReadClickTime: currentTime });
  }

  private loadMore() {
    this.props.userNotificationActions.getUserNotifications(this.state.nextPageToken);
  }

  private handleTabsRef = (tabs: TabsWidget) => {
    // the panel has no interactive element while loading notifications, but after notifications have loaded,
    // we can set focus on the first tab to ensure some element inside the panel is in focus.
    tabs?.setFocusedIndex(0);

    // save the ref so we can set focus on tabs later in case all notification items are removed
    this.tabsRef = tabs;
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    // wrap focus inside the panel when pressing Tab or Shift Tab
    if (e.key === 'Tab' && this.notificationPanelRef.current) {
      const tabbables = tabbable(this.notificationPanelRef.current);
      if (tabbables.length > 0) {
        const firstTabbableElement = tabbables[0];
        const lastTabbableElement = tabbables[tabbables.length - 1];

        if (e.target === firstTabbableElement && e.shiftKey) {
          e.preventDefault();
          lastTabbableElement.focus();
        } else if (e.target === lastTabbableElement && !e.shiftKey) {
          e.preventDefault();
          firstTabbableElement.focus();
        }
      }
    }
  };

  public render() {
    const userSettingUrl = this.props.siteUrls.userSettingsUrl(this.props.userName, this.props.domainName);
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    return <div aria-labelledby={NotificationPanelTitleDomId} id={'notification-panel'} role={'dialog'} data-tb-test-id='notification-panel' onKeyDown={this.handleKeyDown} ref={this.notificationPanelRef} style={NotificationCenterStyles.notificationContainerStyle}>
      <NotificationPanelHeader
        userPreferenceUrl={userSettingUrl}
        isMarkAllReadDisabled={!this.state.notifications || this.state.notifications.length < 1}
        userNotificationActions={this.props.userNotificationActions}
        userNotifications={_.indexBy(this.state.notifications, 'id')}
        displayMode={this.props.displayMode}
        closePanel={this.props.closePanel}
        userId={this.props.userId}
        siteLuid={this.props.siteLuid}
        updateMarkAllAsReadClickTime={this.updateMarkAllAsReadClickTime.bind(this)} />
      <SkeletonLoaderWidget
        isPending={this.props.displayMode === NotificationDisplayMode.NotificationLoading}
        fallback={<NotificationLoader />}
      >
        <div className={NotificationCenterStyles.notificationPanelTab}>
          <TabsWidget
            activationMode={TabActivationMode.Manual}
            anchorTabIndex={this.state.currentTabIndex}
            ariaLabel={Messages.userNotificationTabLabel()}
            initialActiveIndex={0}
            tabListAlignment={TabListAlignment.Left}
            tabs={this.getNotificationTabs()}
            dimensionOverrides={this.tabsDimensionOverrides}
            onActiveIndexChanged={this.onTabChange}
            testId={'notification-center-tab'}
            ref={this.handleTabsRef}
          />
        </div>
      </SkeletonLoaderWidget>
    </div>;
  }

  private renderEmptyNotification(tabState: NotificationStatus) {
    const redundantAltTextDisabled = '';
    return (
      <div data-tb-test-id='empty-notification-panel' style={NotificationCenterStyles.notificationEmptyList}>
        <div> <img src={imgPath('notification_empty_bell.svg')} alt={redundantAltTextDisabled} height="80" /></div>
        <div style={NotificationCenterStyles.notificationEmptyTitle}>{Messages.userNotificationEmptyNotification()}</div>
        {
          tabState === NotificationStatus.ALL && <div style={NotificationCenterStyles.notificationEmptySubtitle}>{Messages.userNotificationEmptyNotificationMessage()}</div>
        }
      </div>);
  }

  private getNotificationTabs(): TabAndPanelContents[] {
    return [{
      panelContent: this.renderNotifications(NotificationStatus.ALL),
      tabContent: { label: Messages.userNotificationAllTabTitle(), title: Messages.userNotificationAllNotifications() }
    }, {
      panelContent: this.renderNotifications(NotificationStatus.UNREAD),
      tabContent: { label: Messages.userNotificationUnreadTabTitle(), title: Messages.userNotificationUnreadNotifications() }
    }];
  }

  private renderNotifications(tabState: NotificationStatus): JSX.Element {
    let notifications = this.getNotificationsForCurrentTab();
    if (this.props.displayMode === NotificationDisplayMode.NotificationLoadFailed) {
      return <NotificationLoadFail />;
    }

    if (!notifications || notifications.length === 0) {
      return this.renderEmptyNotification(tabState);
    }

    let earlierTime = getEarlyNotificationTime();
    const groupedNotifications = _.partition(notifications, (value: IUserNotification<any>) => value.creationTime >= earlierTime);

    return (
      <div style={NotificationCenterStyles.notificationTabContent} data-tb-test-id='notifications-list-container'>
        <InfiniteScroll
          pageStart={0}
          loadMore={this.loadMore.bind(this)}
          initialLoad={true}
          hasMore={this.state.nextPageToken != null}
          useWindow={false}
          isReverse={false}
          threshold={100}
        >
          { // eslint-disable-next-line jsx-a11y/interactive-supports-focus
          }<div
            aria-colcount={2}
            aria-label={tabState === NotificationStatus.ALL ? Messages.userNotificationAllNotifications() : Messages.userNotificationUnreadNotifications()}
            aria-rowcount={notifications.length}
            data-tb-test-id={`notification-tab-${tabState}`}
            onKeyDown={this.handleNotificationsGridKeyDown}
            role='grid'
          >
            {this.renderNotificationSection(NotificationSection.New, groupedNotifications[0])}
            {this.renderNotificationSection(NotificationSection.Earlier, groupedNotifications[1], groupedNotifications[0].length)}
          </div>
        </InfiniteScroll>
      </div>);
  }

  private renderNotificationSection(section: NotificationSection, notifications: Array<IUserNotification<any>>, previousSectionItemCount?: number) {
    let title = section === NotificationSection.Earlier ? Messages.userNotificationEarlierSectionTitle() : Messages.userNotificationNewSectionTitle();
    return notifications.length ? (
      <>
        <div style={notificationStyles.notificationSectionTitle}>{title}</div>
        {
          notifications.map((notification, index) => {
            const notificationProvider = this.notificationContentProviders[notification.type];
            const notificationMenuItemProvider = this.notificationMenuItemProviders[notification.type];
            const indexWithinAllSections = (previousSectionItemCount || 0) + index;
            return <Notification
              key={indexWithinAllSections}
              aria-rowindex={indexWithinAllSections + 1} // aria-rowindex is 1-based, but index is 0-based
              focusable={indexWithinAllSections === this.state.focusableNotificationIndex}
              shouldFocusOnActionButton={this.state.shouldFocusOnActionButton}
              notification={notification}
              notificationProvider={notificationProvider}
              notificationMenuItemProvider={notificationMenuItemProvider}
              siteUrls={this.props.siteUrls}
              onNotificationClick={() => this.onNotificationClick(notification)}
              closePanel={this.props.closePanel}
              userId={this.props.userId}
              siteLuid={this.props.siteLuid}
            />;
          })
        }
      </>) : '';
  }

  private onNotificationClick(notification: IUserNotification<any>) {
    // If the notification url is invalid, do not redirect
    if (isNullOrUndefined(notification.targetInfo?.targetUrl)) return;
    if (!notification.read) {
      this.props.userNotificationActions.updateUserNotificationsStatus([notification.id], true);
    }
    this.props.closePanel();
    Location.instance.setUrl(notification.targetInfo?.targetUrl);
  }

  private handleNotificationsGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const notifications = this.getNotificationsForCurrentTab();
    if (e.keyCode === Keys.ArrowUp) {
      if (this.state.focusableNotificationIndex > 0) {
        this.setState({ focusableNotificationIndex: this.state.focusableNotificationIndex - 1 });
      }
    } else if (e.keyCode === Keys.ArrowDown) {
      if (this.state.focusableNotificationIndex < notifications.length - 1) {
        this.setState({ focusableNotificationIndex: this.state.focusableNotificationIndex + 1 });
      }
    } else if (e.keyCode === Keys.ArrowLeft && document.activeElement.tagName === 'BUTTON') {
      this.setState({ shouldFocusOnActionButton: false });
    } else if (e.keyCode === Keys.ArrowRight && document.activeElement.getAttribute('role') === 'gridcell') {
      this.setState({ shouldFocusOnActionButton: true });
    } else if ((e.keyCode === Keys.Enter || e.keyCode === Keys.Space)
      && document.activeElement.getAttribute('role') === 'gridcell'
      && this.state.focusableNotificationIndex < notifications.length
    ) {
      this.onNotificationClick(notifications[this.state.focusableNotificationIndex]);
    } else {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };
}

export const UserNotificationPanel = errorBoundary<NotificationPanelProps>(sendTelemetryErrorEvent)(NotificationPanel);
